import { motion } from 'framer-motion';
import { Award, Trophy, Target, Flame, Star } from 'lucide-react';

/**
 * Achievement Badges Component
 * Milestone badges and streak tracking
 */
export default function AchievementBadges({ planId, entries = [] }) {
  // Calculate achievements
  const workoutCount = entries.filter(e => e.entry_type === 'workout').length;
  const measurementCount = entries.filter(e => e.entry_type === 'measurement').length;
  const photoCount = entries.filter(e => e.entry_type === 'photo').length;
  const totalEntries = entries.length;

  // Calculate streak (consecutive days with entries)
  const calculateStreak = () => {
    if (entries.length === 0) return 0;
    const sortedEntries = [...entries].sort((a, b) => 
      new Date(b.entry_date) - new Date(a.entry_date)
    );
    
    let streak = 0;
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    for (const entry of sortedEntries) {
      const entryDate = new Date(entry.entry_date);
      entryDate.setHours(0, 0, 0, 0);
      
      const daysDiff = Math.floor((currentDate - entryDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === streak) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else if (daysDiff > streak) {
        break;
      }
    }
    
    return streak;
  };

  const streak = calculateStreak();

  const achievements = [
    {
      id: 'first_workout',
      name: 'First Workout',
      icon: Target,
      unlocked: workoutCount >= 1,
      description: 'Complete your first workout',
    },
    {
      id: 'workout_warrior',
      name: 'Workout Warrior',
      icon: Trophy,
      unlocked: workoutCount >= 10,
      description: 'Complete 10 workouts',
    },
    {
      id: 'measurement_master',
      name: 'Measurement Master',
      icon: Star,
      unlocked: measurementCount >= 5,
      description: 'Log 5 measurements',
    },
    {
      id: 'photo_pro',
      name: 'Photo Pro',
      icon: Award,
      unlocked: photoCount >= 3,
      description: 'Upload 3 progress photos',
    },
    {
      id: 'streak_starter',
      name: 'Streak Starter',
      icon: Flame,
      unlocked: streak >= 7,
      description: '7 day activity streak',
    },
    {
      id: 'consistency_king',
      name: 'Consistency King',
      icon: Trophy,
      unlocked: streak >= 30,
      description: '30 day activity streak',
    },
  ];

  const unlockedAchievements = achievements.filter(a => a.unlocked);
  const lockedAchievements = achievements.filter(a => !a.unlocked);

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Achievements</h3>
        <div className="text-right">
          <p className="text-2xl font-bold text-primary-600">{streak}</p>
          <p className="text-sm text-gray-600">Day Streak</p>
        </div>
      </div>

      {/* Unlocked Achievements */}
      {unlockedAchievements.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Unlocked</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {unlockedAchievements.map((achievement) => {
              const Icon = achievement.icon;
              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4 border-2 border-primary-300"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{achievement.name}</p>
                      <p className="text-xs text-gray-600">{achievement.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Locked Achievements */}
      {lockedAchievements.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Locked</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {lockedAchievements.map((achievement) => {
              const Icon = achievement.icon;
              return (
                <div
                  key={achievement.id}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200 opacity-60"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-600">{achievement.name}</p>
                      <p className="text-xs text-gray-500">{achievement.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {achievements.length === 0 && (
        <p className="text-gray-600 text-center py-4">No achievements available</p>
      )}
    </div>
  );
}
